// Zustand store for the platform super-admin session.
//
// COMPLETELY SEPARATE from `authStore`. Super admins live in
// `tricivo_admins` (server table) — they are not tenant staff. The
// realm='super' JWT minted by /api/v1/auth/super/login cannot be used on
// tenant endpoints, and tenant staff JWTs cannot reach /super/*.
//
// Wave I additionally moves the impersonation banner state here. While
// impersonating, the super-admin token is stashed in
// `stashedSuperAccessToken` and the (realm='staff') impersonation token
// takes over the tenant `accessToken` storage slot. `stopImpersonation()`
// restores the super token to `superAccessToken`.
//
// Storage namespacing: the super-admin token lives in `superAccessToken`
// (`tricivo_super_access_token`), separate from the tenant `accessToken`
// slot, so the two realms can coexist in the same browser without
// evicting each other. The store never reads or writes the tenant-only
// keys (`companyId`, the legacy `tricivo_user`).

import { create } from 'zustand';
import { storage } from '../utils/storage';

export type SuperAdminStatus = 'loading' | 'authenticated' | 'anonymous';

export interface SuperAdminSummary {
  name: string;
  email: string;
}

export interface ImpersonatedCompany {
  enc_id: string;
  name: string;
}

export interface SuperAdminState {
  status: SuperAdminStatus;
  admin: SuperAdminSummary | null;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  impersonatedCompany: ImpersonatedCompany | null;
  /** Snapshot of the super-admin access token at the moment impersonation
   *  began. The token stays in its own storage slot the whole time; this
   *  field exists so the UI can show it was a real (non-empty) session
   *  and so existing call sites/tests can continue to assert non-null. */
  stashedSuperAccessToken: string | null;
  /** Snapshot of the `realm` storage slot at impersonation start. Restored
   *  (or cleared if it was unset) on stop so the slot doesn't leak the
   *  impersonation marker. */
  stashedRealm: string | null;
}

export interface SuperAdminActions {
  setLoading: () => void;
  setAuthenticated: (admin: SuperAdminSummary) => void;
  setAnonymous: () => void;
  reset: () => void;
  startImpersonation: (
    impersonationAccessToken: string,
    company: ImpersonatedCompany,
  ) => void;
  stopImpersonation: () => void;
}

export type SuperAdminStore = SuperAdminState & SuperAdminActions;

const initialState: SuperAdminState = {
  status: 'loading',
  admin: null,
  isAuthenticated: false,
  isImpersonating: false,
  impersonatedCompany: null,
  stashedSuperAccessToken: null,
  stashedRealm: null,
};

export const useSuperAdminStore = create<SuperAdminStore>((set) => ({
  ...initialState,

  setLoading: () => set({ status: 'loading' }),

  setAuthenticated: (admin) =>
    set({ status: 'authenticated', admin, isAuthenticated: true }),

  setAnonymous: () =>
    set({
      status: 'anonymous',
      admin: null,
      isAuthenticated: false,
      isImpersonating: false,
      impersonatedCompany: null,
      stashedSuperAccessToken: null,
      stashedRealm: null,
    }),

  /** Test-only helper. Returns the store to its boot-time state ('loading')
   *  so the SuperProtectedRoute hydration effect runs again. Tests that
   *  want an explicit "logged out" state should call `setAnonymous()`. */
  reset: () => set({ ...initialState }),

  /**
   * Stash the current super access token, write the impersonation token
   * (realm='staff') into the tenant `accessToken` storage slot, and flip
   * the impersonation flags. The axios client routes tenant URLs to the
   * tenant slot and super URLs to the super slot, so the impersonated
   * tenant calls pick up the impersonation token automatically.
   */
  startImpersonation: (impersonationAccessToken, company) =>
    set(() => {
      const realToken = storage.get('superAccessToken');
      if (typeof realToken === 'string' && realToken.length > 0) {
        const priorRealm = storage.get('realm');
        storage.set('accessToken', impersonationAccessToken);
        // Mark the tenant realm slot so AuthProvider treats the boot as a
        // tenant session on reload during impersonation.
        storage.set('realm', 'staff');
        return {
          isImpersonating: true,
          impersonatedCompany: company,
          stashedSuperAccessToken: realToken,
          stashedRealm: priorRealm,
        };
      }
      // No real super token? Refuse to enter impersonation — the user
      // would lose access entirely on stop.
      return {};
    }),

  /**
   * Clear the impersonation token from the tenant slot, restore the prior
   * realm marker, and clear the impersonation flags. The super token
   * never left `superAccessToken`, so no restore step is needed there.
   * Caller is responsible for navigation.
   */
  stopImpersonation: () =>
    set((state) => {
      // The impersonation token lived in the tenant slot — clear it so a
      // background tenant request doesn't authenticate as the impersonated
      // user after stop. The super token in `superAccessToken` is
      // untouched.
      storage.remove('accessToken');
      if (state.stashedRealm !== null) {
        storage.set('realm', state.stashedRealm);
      } else {
        storage.remove('realm');
      }
      return {
        isImpersonating: false,
        impersonatedCompany: null,
        stashedSuperAccessToken: null,
        stashedRealm: null,
      };
    }),
}));

// One-time cleanup of pre-typed-wrapper localStorage keys (tricivo_token,
// tricivo_user). Idempotent and side-effecting at module load — runs once
// per page load when this store is imported. Mirrored in `authStore`.
storage.removeLegacyKeys();
